// The two rules players get wrong, as pure functions the screens share: the
// budget meter (solid = points delivered, hatched = the doubling surcharge) and
// the guess bijection (each name used once). Ported from the vote/guess
// prototypes, generalised to read every number from the ruleset economy so
// nothing is hardcoded (rule 1). The API re-validates all of this; these
// functions exist to make the UI honest before it submits.

import type { BallotEntry, Ruleset as Economy } from './types';

/** One track's slice of the caller's ballot. */
export interface Dim {
  points: number;
  guessed_user_id: number | null;
  is_veto: boolean;
}

/** submission_id → dim, for the votable tracks only (never the caller's own). */
export type Draft = Map<number, Dim>;

export const emptyDim = (): Dim => ({ points: 0, guessed_user_id: null, is_veto: false });

/** Seed a draft from an existing server draft, defaulting every votable track. */
export function draftFromEntries(entries: BallotEntry[], votableIds: number[]): Draft {
  const byId = new Map(entries.map((e) => [e.submission_id, e]));
  const d: Draft = new Map();
  for (const id of votableIds) {
    const e = byId.get(id);
    d.set(id, e ? { points: e.points, guessed_user_id: e.guessed_user_id, is_veto: e.is_veto } : emptyDim());
  }
  return d;
}

/** A full ballot payload: one entry per votable track. */
export function draftToEntries(draft: Draft): BallotEntry[] {
  return [...draft.entries()].map(([submission_id, dim]) => ({
    submission_id,
    points: dim.points,
    guessed_user_id: dim.guessed_user_id,
    is_veto: dim.is_veto,
  }));
}

// ---- Budget meter ---------------------------------------------------------

/** What a points value costs against the budget: a mark-down is charged double. */
export function cost(points: number, eco: Economy): number {
  return points >= 0 ? points : Math.abs(points) * eco.negative_multiplier;
}

export function budgetSpent(draft: Draft, eco: Economy): number {
  let s = 0;
  for (const dim of draft.values()) s += cost(dim.points, eco);
  return s;
}

/** The portion of the budget lost purely to the doubling surcharge. */
export function surcharge(draft: Draft, eco: Economy): number {
  let s = 0;
  for (const dim of draft.values()) {
    if (dim.points < 0) s += Math.abs(dim.points) * (eco.negative_multiplier - 1);
  }
  return s;
}

export type SegKind = 'spent' | 'pen' | 'empty';

/**
 * `budget` segments: the first `solid` are delivered points, the next
 * `surcharge` are the hatched doubling penalty, the rest empty. This is the
 * visual the spec says to keep exactly.
 */
export function meterSegments(draft: Draft, eco: Economy): SegKind[] {
  const spent = budgetSpent(draft, eco);
  const sur = surcharge(draft, eco);
  const solid = spent - sur;
  return Array.from({ length: eco.budget }, (_, n) =>
    n < solid ? 'spent' : n < spent ? 'pen' : 'empty',
  );
}

/** Can this track move by `delta` without leaving the range or busting budget? */
export function canStep(draft: Draft, id: number, delta: number, eco: Economy): boolean {
  const dim = draft.get(id);
  if (!dim) return false;
  const next = dim.points + delta;
  if (next < eco.track_min || next > eco.track_max) return false;
  const after = budgetSpent(draft, eco) - cost(dim.points, eco) + cost(next, eco);
  return after <= eco.budget;
}

export const pointsComplete = (draft: Draft, eco: Economy): boolean =>
  budgetSpent(draft, eco) === eco.budget;

// ---- Veto -----------------------------------------------------------------

export function vetoCount(draft: Draft): number {
  let n = 0;
  for (const dim of draft.values()) if (dim.is_veto) n++;
  return n;
}

/** Rule 5: a veto may not sit on a track you gave positive points. */
export const vetoEligible = (dim: Dim | undefined): boolean => !!dim && dim.points <= 0;

// ---- Guess bijection ------------------------------------------------------

/** user_ids already spent on other tracks (so a name is offered at most once). */
export function takenGuesses(draft: Draft, exceptId?: number): Set<number> {
  const taken = new Set<number>();
  for (const [id, dim] of draft.entries()) {
    if (id === exceptId) continue;
    if (dim.guessed_user_id != null) taken.add(dim.guessed_user_id);
  }
  return taken;
}

export function guessesComplete(draft: Draft): boolean {
  const seen = new Set<number>();
  for (const dim of draft.values()) {
    if (dim.guessed_user_id == null) return false;
    if (seen.has(dim.guessed_user_id)) return false;
    seen.add(dim.guessed_user_id);
  }
  return true;
}

// ---- Whole-ballot readiness ----------------------------------------------

/**
 * True when the assembled ballot would pass the server's submit-only rules, so
 * the screen can auto-finalise (submit:true) the moment the last of Vote/Veto/
 * Guess completes — on whichever screen the player finishes. Otherwise it saves
 * a draft (submit:false) and says the ballot is sent once all three are done.
 */
export function isSubmittable(draft: Draft, eco: Economy): boolean {
  if (draft.size === 0) return false;
  if (!pointsComplete(draft, eco)) return false;
  // The veto is optional: you may abstain (0) or cast exactly one — never more.
  if (vetoCount(draft) > 1) return false;
  if (!guessesComplete(draft)) return false;
  for (const dim of draft.values()) {
    if (dim.is_veto && dim.points > 0) return false;
  }
  return true;
}
